import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  enableProdMode,
  isDevMode,
  provideExperimentalZonelessChangeDetection,
} from '@angular/core';
import { getAnalytics, provideAnalytics } from '@angular/fire/analytics';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import {
  connectAuthEmulator,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  provideAuth,
} from '@angular/fire/auth';
import {
  connectFirestoreEmulator,
  getFirestore,
  provideFirestore,
} from '@angular/fire/firestore';
import {
  connectFunctionsEmulator,
  getFunctions,
  provideFunctions,
} from '@angular/fire/functions';
import {
  connectStorageEmulator,
  getStorage,
  provideStorage,
} from '@angular/fire/storage';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  PreloadAllModules,
  provideRouter,
  RouteReuseStrategy,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { initialize as initializeSafeArea } from '@capacitor-community/safe-area';
import { Capacitor } from '@capacitor/core';
import { FEATURE_KEY, reducers } from '@http/store/feature-state';
import { HangEffects } from '@http/store/hang/hang-effects';
import { InterestEffects } from '@http/store/interest/interest-effects';
import { PuebloEffects } from '@http/store/pueblo/pueblo-effects';
import { UserEffects } from '@http/store/user/user-effects';
import {
  IonicRouteStrategy,
  provideIonicAngular,
} from '@ionic/angular/standalone';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';
import { APP_ROUTES } from '@routing/app.routes';
import { AppComponent } from 'app/app.component';
import { environment } from './environments/environment';

defineCustomElements(window);

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 5000 } },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: { touchLongPressShowDelay: 0, hideDelay: 3000 },
    },
    provideNativeDateAdapter(),
    provideExperimentalZonelessChangeDetection(),
    provideIonicAngular({ mode: 'md' }),
    provideRouter(
      APP_ROUTES,
      withPreloading(PreloadAllModules),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
    ),
    provideStore(),
    provideState(FEATURE_KEY, reducers),
    provideEffects([
      PuebloEffects,
      HangEffects,
      UserEffects,
      InterestEffects,
      PuebloEffects,
    ]),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideFirebaseApp(() => initializeApp(environment.firebase.options)),
    provideAnalytics(() => getAnalytics()),
    provideFunctions(() => {
      const functions = getFunctions();
      if (!environment.production) {
        connectFunctionsEmulator(
          functions,
          environment.firebase.localServer,
          5001,
        );
      }
      return functions;
    }),
    provideFirestore(() => {
      const firestore = getFirestore();
      if (!environment.production) {
        connectFirestoreEmulator(
          firestore,
          environment.firebase.localServer,
          8080,
        );
      }
      return firestore;
    }),
    provideAuth(() => {
      const auth = Capacitor.isNativePlatform()
        ? initializeAuth(getApp(), { persistence: indexedDBLocalPersistence })
        : getAuth();
      if (!environment.production) {
        connectAuthEmulator(
          auth,
          `http://${environment.firebase.localServer}:9099`,
        );
      }
      return auth;
    }),
    provideStorage(() => {
      const storage = getStorage();
      if (!environment.production) {
        connectStorageEmulator(storage, environment.firebase.localServer, 9199);
      }
      return storage;
    }),
    environment.providers,
  ],
}).then(() => {
  initializeSafeArea();
});
